<template>
  <b-card v-if="submission">
    <template #header>
      <span class="font-weight-bold">{{ title }}</span>
      <b-badge
        class="ml-2"
        pill
        variant="dark"
        v-for="(v, k) in titlePills"
        :key="k"
        >{{ v }}</b-badge
      >
    </template>
    <b-row>
      <b-col>
        <sample-review-card
          v-for="(sample, index) in submission.samples"
          :key="index"
          :sample="sample"
          :sampleType="sample_type"
          :types="generateTypes()"
          :list="list"
          :subList="sub_list"
          :managedLists="managedLists"
        />

        <template v-if="submission.submission_soil && submission.submission_soil.projections">
          <div class="my-3 font-weight-bold">
            Fertilizer Recommendations
          </div>
          <b-card
            v-for="(projection, index) in submission.submission_soil.projections"
            :key="index"
            no-body
            border-variant="light"
            class="border-top-0 border-right-0 border-left-0 border-bottom rounded-0 mb-0"
          >
            <b-card-body class="mb-1 p-0">
              <template v-if="projection.crop">
                <span class="mx-r">
                  <span class="font-weight-bold">Crop:</span>
                  {{ projection.crop }}
                </span>
              </template>
              <template v-if="projection.crop">
                <span class="mx-2">
                  Unit: {{ projection.unit }}
                </span>
              </template>
              <template v-if="projection.yield">
                <span class="mx-2">
                  Yield: {{ projection.yield }}
                </span>
              </template>
            </b-card-body>
          </b-card>
        </template>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import SampleReviewCard from "./SampleReviewCard.vue";
import SampleTypeModel from "@/models/lab/SampleTypeModel";
import TestPackageModel from "@/models/lab/TestPackageModel";
import TurnaroundModel from "@/models/lab/TurnaroundModel";

export default {
  name: "SubmissionReviewCard",
  components: { SampleReviewCard },
  props: {
    submission: {
      type: Object,
      default: () => null,
    },
    managedLists: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      sample_type: null,
      test_package: null,
      turnaround: null,
      list: null,
      sub_list: null,
    };
  },
  computed: {
    title() {
      const identifier =
        [
          this.submission.identifier || null,
          this.submission.event_code
            ? `(#${this.submission.event_code})`
            : null,
        ]
          .filter((i) => !!i)
          .join(" ") || null;
      const type_data =
        [
          this.isSoil && this.submission.submission_soil.zone
            ? this.submission.submission_soil.zone
            : null,
          this.isSoil && this.submission.submission_soil.grower
            ? `Grower: ${this.submission.submission_soil.grower}`
            : null,
          this.isSoil && this.submission.submission_soil.irrigated
            ? "Irrigated"
            : null,
          this.isTissue && this.submission.submission_tissue.crop
            ? `Crop: ${
                this.list.attribute_lists.find(
                  (item) => item.id === this.submission.submission_tissue.crop
                ).value
              }`
            : null,
          this.isTissue && this.submission.submission_tissue.cropstage
            ? `Crop stage: ${
                this.sub_list.attribute_lists.find(
                  (item) =>
                    item.id === this.submission.submission_tissue.cropstage
                ).value
              }`
            : null,
          this.isPotato && this.submission.submission_potato.grower
            ? `Grower: ${this.submission.submission_potato.grower}`
            : null,
          this.isPotato && this.submission.submission_potato.storage_bin
            ? `Storage bin: ${this.submission.submission_potato.storage_bin}`
            : null,
          this.isMixer &&
          this.submission.submission_feed_mixer_efficiency.all_sample_types
            ? this.submission.submission_feed_mixer_efficiency.all_sample_types
            : null,
        ]
          .filter((i) => i)
          .join(", ") || null;

      return (
        [identifier || null, type_data || null].filter((i) => !!i).join(", ") ||
        null
      );
    },
    titlePills() {
      return (
        [
          this.sample_type ? this.sample_type.name : null,
          this.test_package ? this.test_package.name : null,
          this.turnaround ? this.turnaround.name : null,
        ].filter((i) => !!i) || []
      );
    },
    getTestPackageName() {
      return this.test_package ? this.test_package.name : "";
    },
    getSampleTypeName() {
      return this.sample_type ? this.sample_type.name : "";
    },
    isSoil() {
      return this.getSampleTypeName.toLowerCase() === "soil";
    },
    isMixer() {
      return this.getSampleTypeName.toLowerCase() === "feed mixer efficiency";
    },
    isFeed() {
      return this.getSampleTypeName.toLowerCase() === "feed";
    },
    isPotato() {
      return this.getSampleTypeName.toLowerCase() === "potato";
    },
    isTissue() {
      return this.getSampleTypeName.toLowerCase() === "tissue";
    },
    isWater() {
      return this.getSampleTypeName.toLowerCase() === "water";
    },
    isIrrigationSuitability() {
      return this.getSampleTypeName.toLowerCase() === "irrigation suitability";
    },
    isSepticSoil() {
      return this.getSampleTypeName.toLowerCase() === "septic soil";
    },
    isGeneral() {
      return !(
        this.isSoil ||
        this.isTissue ||
        this.isSepticSoil ||
        this.isMixer ||
        this.isFeed ||
        this.isPotato ||
        this.isWater ||
        this.isIrrigationSuitability
      );
    },
  },
  methods: {
    generateTypes() {
      const types = {
        main_type: null,
        type_one: null,
        type_two: null,
        field: null,
        sub_field_one: null,
        sub_field_two: null,
      };
      if (this.isTissue) {
        types.main_type = "tissue";
        types.type_one = "type";
        types.type_two = "growth_stage";
      }
      if (this.isWater) {
        types.main_type = "water";
        types.type_one = "usage";
        types.field = "sample_water";
        types.sub_field_one = "usage";
      }
      if (this.isFeed) {
        types.main_type = "feed";
        types.type_one = "type";
        types.type_two = "animal_fed";
        types.field = "sample_feeds";
        types.sub_field_one = "type";
        types.sub_field_two = "animal_fed";
      }
      if (this.isIrrigationSuitability) {
        types.main_type = "irrigation_suitability";
        types.type_one = "texture";
        types.type_two = "horizon";
        types.field = "sample_irrigation_suitability";
        types.sub_field_one = "field_texture";
        types.sub_field_two = "soil_horizon";
      }

      return types;
    },
    generateLists() {
      const { main_type, type_one, type_two } = this.generateTypes();
      this.list = this.managedLists.find(
        (item) => item.sub_type === main_type && item.list === type_one
      );
      this.sub_list = this.managedLists.find(
        (item) => item.sub_type === main_type && item.list === type_two
      );
    },
  },
  watch: {
    "submission.sample_type_id": {
      handler(id) {
        const entity = new SampleTypeModel({ id });

        entity.fetch().then(() => {
          this.sample_type = entity;
          this.generateLists();
        });
      },
      immediate: true,
      deep: true,
    },
    "submission.test_package_id": {
      handler(id) {
        const entity = new TestPackageModel({ id });

        entity.fetch().then(() => {
          this.test_package = entity;
        });
      },
      immediate: true,
      deep: true,
    },
    "submission.turnaround_id": {
      handler(id) {
        if (id) {
          const entity = new TurnaroundModel({ id });

          entity.fetch().then(() => {
            this.turnaround = entity;
          });
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>
